import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { track } from "@/lib/analytics";
import { ArrowRight, Check } from "lucide-react";

const field = "w-full bg-transparent border-b border-white/25 py-3 font-body text-ink placeholder-white/45 focus:border-gold focus:outline-none transition-colors";

export default function PassportListForm({ sourcePage = "Thailand2027", campaignSource = "", compact = false }) {
  const [form, setForm] = useState({ first_name: "", email: "", website: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) { toast.error("Please enter your email address."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/subscribers", { ...form, source_page: sourcePage, campaign_source: campaignSource });
      track("join_passport_list", { source_page: sourcePage });
      toast.success(data.message || "You're on the list.");
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally { setLoading(false); }
  };

  // Compact inline variant (e.g. homepage band) — same logic, slimmer layout.
  if (compact) {
    return (
      <div data-testid="passport-list">
        {done ? (
          <div data-testid="passport-list-success" className="flex items-center gap-3 text-gold">
            <span className="w-9 h-9 border border-gold/50 flex items-center justify-center shrink-0"><Check size={16} /></span>
            <span className="font-serif text-xl font-light">You're on the list. Watch your inbox for what's next.</span>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="passport-list-form">
            <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" value={form.website} onChange={set("website")} className="hidden" name="website" />
            <div className="flex flex-col sm:flex-row sm:items-end gap-6">
              <div className="flex-1">
                <label htmlFor="plc-first" className="block font-body text-[11px] uppercase tracking-[0.15em] text-white/40 mb-1">First Name</label>
                <input id="plc-first" data-testid="pl-first-name" className={field} value={form.first_name} onChange={set("first_name")} />
              </div>
              <div className="flex-1">
                <label htmlFor="plc-email" className="block font-body text-[11px] uppercase tracking-[0.15em] text-white/40 mb-1">Email Address</label>
                <input id="plc-email" data-testid="pl-email" type="email" inputMode="email" autoComplete="email" className={field} value={form.email} onChange={set("email")} />
              </div>
              <button data-testid="pl-submit" type="submit" disabled={loading}
                className="group inline-flex items-center justify-center gap-3 border border-gold bg-gold text-black px-8 py-3.5 font-body text-xs uppercase tracking-[0.22em] hover:bg-transparent hover:text-gold transition-colors duration-300 disabled:opacity-50 shrink-0">
                {loading ? "Joining..." : "Join"} <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
            <p className="font-body text-[11px] text-white/45 leading-relaxed font-light mt-4">
              By joining, you're opting in to marketing emails from Inforno Travel. Unsubscribe anytime. <Link to="/privacy" className="text-gold link-underline">Privacy Policy</Link>.
            </p>
          </form>
        )}
      </div>
    );
  }

  return (
    <div data-testid="passport-list" className="border border-gold/30 bg-gradient-to-br from-[#12100a] to-noir p-8 md:p-12">
      <p className="font-body text-[11px] uppercase tracking-[0.4em] text-gold mb-4">The Passport List</p>
      <h3 className="font-serif text-3xl md:text-4xl font-light leading-[0.98]">Where Will Your Passport Take You Next?</h3>
      <p className="font-body text-white/60 mt-4 text-sm md:text-base font-light max-w-xl">
        Join the Passport List for first access to new Passport Experiences, curated trip opportunities, travel
        inspiration and updates from Inforno Travel.
      </p>

      {done ? (
        <div data-testid="passport-list-success" className="mt-8 flex items-center gap-3 text-gold">
          <span className="w-9 h-9 border border-gold/50 flex items-center justify-center"><Check size={16} /></span>
          <span className="font-serif text-xl font-light">You're on the list. Watch your inbox for what's next.</span>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-xl" data-testid="passport-list-form">
          {/* honeypot (hidden from users) */}
          <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" value={form.website} onChange={set("website")}
            className="hidden" name="website" />
          <div>
            <label htmlFor="pl-first" className="block font-body text-[11px] uppercase tracking-[0.15em] text-white/40 mb-1">First Name</label>
            <input id="pl-first" data-testid="pl-first-name" className={field} value={form.first_name} onChange={set("first_name")} />
          </div>
          <div>
            <label htmlFor="pl-email" className="block font-body text-[11px] uppercase tracking-[0.15em] text-white/40 mb-1">Email Address</label>
            <input id="pl-email" data-testid="pl-email" type="email" inputMode="email" autoComplete="email" className={field} value={form.email} onChange={set("email")} />
          </div>
          <button data-testid="pl-submit" type="submit" disabled={loading}
            className="sm:col-span-2 group inline-flex items-center justify-center gap-3 border border-gold bg-gold text-black px-8 py-4 font-body text-xs uppercase tracking-[0.22em] hover:bg-transparent hover:text-gold transition-colors duration-300 disabled:opacity-50">
            {loading ? "Joining..." : "Join the Passport List"} <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
          </button>
          <p className="sm:col-span-2 font-body text-xs text-white/60 leading-relaxed font-light">
            By joining the Passport List, you're opting in to receive trip ideas, travel news and marketing emails from Inforno Travel. You can unsubscribe at any time. See our <Link to="/privacy" className="text-gold link-underline">Privacy Policy</Link>.
          </p>
        </form>
      )}
    </div>
  );
}
